import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Stack,
  Paper,
  IconButton,
  Tooltip,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Badge,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Warning as WarningIcon,
  Vaccines as VaccineIcon,
  LocalHospital as HospitalIcon,
  Notifications as NotificationIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  MarkEmailRead as MarkReadIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  MoreVert as MoreIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { notificationsService, Notification } from '../services/notificationsService';

const Notifications: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [filteredNotifications, setFilteredNotifications] = useState<Notification[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);

  useEffect(() => {
    loadNotifications();
  }, [user?.clinicId]);

  useEffect(() => {
    filterNotifications();
  }, [notifications, tabValue, typeFilter, searchQuery]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await notificationsService.getNotifications({
        clinicId: user?.clinicId || '',
        limit: 100
      });
      // Ensure channels is always an array
      const notificationsWithChannels = (response.notifications || []).map(notif => ({
        ...notif,
        channels: notif.channels || []
      }));
      setNotifications(notificationsWithChannels);
    } catch (error) {
      console.error('Eroare la încărcarea notificărilor:', error);
      setError('Notificările nu au putut fi încărcate. Verifică conexiunea și încearcă din nou.');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const filterNotifications = () => {
    let filtered = [...notifications];

    // Filter by tab (read/unread/all)
    if (tabValue === 1) {
      filtered = filtered.filter(n => !n.read);
    } else if (tabValue === 2) {
      filtered = filtered.filter(n => n.read);
    }

    // Filter by type
    if (typeFilter !== 'all') {
      filtered = filtered.filter(n => n.type === typeFilter);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(n =>
        n.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        n.message.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredNotifications(filtered);
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'appointment_reminder': return <CalendarIcon />;
      case 'vaccination_due': return <VaccineIcon />;
      case 'treatment_reminder': return <HospitalIcon />;
      case 'payment_confirmation': return <MoneyIcon />;
      case 'emergency_alert': return <WarningIcon />;
      default: return <NotificationIcon />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'appointment_reminder': return 'primary';
      case 'vaccination_due': return 'warning';
      case 'treatment_reminder': return 'info';
      case 'payment_confirmation': return 'success';
      case 'emergency_alert': return 'error';
      default: return 'default';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'appointment_reminder': return 'Programare';
      case 'vaccination_due': return 'Vaccinare';
      case 'treatment_reminder': return 'Tratament';
      case 'payment_confirmation': return 'Plată';
      case 'emergency_alert': return 'Urgență';
      case 'general': return 'General';
      default: return type;
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      await notificationsService.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId ? { ...notif, read: true } : notif
        )
      );
    } catch (error) {
      console.error('Eroare la marcarea notificării ca citită:', error);
      // Update locally even if backend fails
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId ? { ...notif, read: true } : notif
        )
      );
    }
  };

  const markAllAsRead = async () => {
    try {
      const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
      await Promise.all(unreadIds.map(id => notificationsService.markAsRead(id)));
      setNotifications(prev =>
        prev.map(notif => ({ ...notif, read: true }))
      );
    } catch (error) {
      console.error('Eroare la marcarea tuturor notificărilor ca citite:', error);
      // Update locally even if backend fails
      setNotifications(prev =>
        prev.map(notif => ({ ...notif, read: true }))
      );
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      await notificationsService.delete(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      handleCloseMenu();
    } catch (error) {
      console.error('Eroare la ștergerea notificării:', error);
      // Delete locally even if backend fails
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      handleCloseMenu();
    }
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, notification: Notification) => {
    setAnchorEl(event.currentTarget);
    setSelectedNotification(notification);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedNotification(null);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Se încarcă notificările...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Notificări
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gestionează notificările clinicii tale
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<MarkReadIcon />}
            onClick={markAllAsRead}
            disabled={unreadCount === 0}
          >
            Marchează toate ca citite
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadNotifications}
          >
            Actualizează
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            {/* Search */}
            <TextField
              placeholder="Caută notificări..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* Type Filter */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Tip notificare</InputLabel>
              <Select
                value={typeFilter}
                label="Tip notificare"
                onChange={(e) => setTypeFilter(e.target.value)}
                startAdornment={<FilterIcon sx={{ mr: 1, ml: 1 }} />}
              >
                <MenuItem value="all">Toate</MenuItem>
                <MenuItem value="appointment_reminder">Programări</MenuItem>
                <MenuItem value="vaccination_due">Vaccinări</MenuItem>
                <MenuItem value="treatment_reminder">Tratamente</MenuItem>
                <MenuItem value="payment_confirmation">Plăți</MenuItem>
                <MenuItem value="emergency_alert">Urgențe</MenuItem>
                <MenuItem value="general">Generale</MenuItem>
              </Select>
            </FormControl>

            {/* Stats */}
            <Box sx={{ ml: 'auto', display: 'flex', gap: 2 }}>
              <Chip
                label={`Total: ${notifications.length}`}
                color="default"
                variant="outlined"
              />
              <Chip
                label={`Necitite: ${unreadCount}`}
                color="primary"
                variant="filled"
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Toate" />
          <Tab
            label={
              <Badge badgeContent={unreadCount} color="error">
                Necitite
              </Badge>
            }
          />
          <Tab label="Citite" />
        </Tabs>
      </Card>

      {/* Notifications List */}
      {error && notifications.length === 0 ? (
        <Alert severity="error">
          Notificările nu sunt disponibile momentan. Empty state-ul nu mai maschează o eroare de API.
        </Alert>
      ) : filteredNotifications.length === 0 ? (
        <Alert severity="info">
          Nu există notificări pentru filtrele selectate.
        </Alert>
      ) : (
        <Stack spacing={2}>
          {filteredNotifications.map((notification) => (
            <Paper
              key={notification.id}
              sx={{
                p: 3,
                opacity: notification.read ? 0.7 : 1,
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  bgcolor: 'action.hover',
                  transform: 'translateY(-2px)',
                  boxShadow: 3,
                },
              }}
              onClick={() => !notification.read && markAsRead(notification.id)}
            >
              <Box display="flex" alignItems="flex-start" gap={2}>
                {/* Avatar */}
                <Avatar
                  sx={{
                    bgcolor: `${getNotificationColor(notification.type)}.main`,
                    width: 48,
                    height: 48,
                  }}
                >
                  {getNotificationIcon(notification.type)}
                </Avatar>

                {/* Content */}
                <Box flex={1}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: notification.read ? 400 : 600,
                          color: notification.read ? 'text.secondary' : 'text.primary',
                          fontSize: '1.1rem',
                        }}
                      >
                        {notification.title}
                      </Typography>
                      <Box display="flex" gap={1} mt={0.5}>
                        <Chip
                          label={getTypeLabel(notification.type)}
                          color={getNotificationColor(notification.type) as any}
                          size="small"
                          variant="outlined"
                        />
                        {!notification.read && (
                          <Chip
                            icon={<CheckIcon />}
                            label="Nou"
                            color="primary"
                            size="small"
                            variant="filled"
                          />
                        )}
                      </Box>
                    </Box>

                    {/* Actions */}
                    <Box display="flex" alignItems="center" gap={1}>
                      {!notification.read && (
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                          }}
                        />
                      )}
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenMenu(e, notification);
                        }}
                      >
                        <MoreIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Message */}
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    {notification.message}
                  </Typography>

                  {/* Footer */}
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      {notification.createdAt ? new Date(notification.createdAt).toLocaleDateString('ro-RO', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      }) : 'N/A'}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {notification.channels && notification.channels.length > 0 && notification.channels.map((channel) => (
                        <Chip
                          key={channel}
                          label={channel.toUpperCase()}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        {selectedNotification && !selectedNotification.read && (
          <MenuItem
            onClick={() => {
              markAsRead(selectedNotification.id);
              handleCloseMenu();
            }}
          >
            <ListItemIcon>
              <MarkReadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Marchează ca citită</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            // Archive functionality can be added here
            handleCloseMenu();
          }}
        >
          <ListItemIcon>
            <ArchiveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Arhivează</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => selectedNotification && deleteNotification(selectedNotification.id)}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Șterge</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Notifications;
